import React, { useEffect, useContext, useState } from "react";
import useSnackbar from "../../hooks/snackbar/useSnackbar";
import {
  BrowserRouter as Router,
  RouteComponentProps,
  Prompt,
} from "react-router-dom";
import UserContext from "../../UserContext";
import StepperComponent from "../shared/common/StepperComponent";
import FinancingRequestBasicInfoStep from "./basic-info/FinancingRequestBasicInfoStep";
import FinancingRequestStudentStep from "./student/FinancingRequestStudentStep";
import FinancingRequestStudentDataStep from "./student-data/FinancingRequestStudentDataStep";
import FinancingRequestDataConfirmationStep from "./data-confirmation/FinancingRequestDataConfirmationStep";
import FinancingRequestDataSubmissionStep from "./data-submission/FinancingRequestDataSubmissionStep";
import FinancingRequest from "../../models/financing-request/FinancingRequest";
import { FinancingRequestBasicInfo } from "../../models/financing-request/FinancingRequestBasicInfo";
import { SchoolCarrier } from "../../models/financing-request/SchoolCarrier";
import FinancingRequestStudent from "../../models/financing-request/FinancingRequestStudent";
import FinancingRequestStudentData from "../../models/financing-request/student-data/FinancingRequestStudentData";
import FinancingRequestApiClient from "../../api/FinancingRequestApiClient";
import { financingRequestsUrl } from "../../urls";
import { TransportOptionType } from "../../models/transport-option-type/TransportOptionType";
import SchoolCarrierTransportOption from "../../models/financing-request/transport-option/SchoolCarrierTransportOption";
import PersonalTransportOption from "../../models/financing-request/transport-option/PersonalTransportOption";
import PersonalPassengerTransportOption from "../../models/financing-request/transport-option/PersonalPassengerTransportOption";
import useStudentDataInfo from "../../hooks/student-data-info/useStudentDataInfo";
import TrSnackbar from "../shared/controls/TrSnackbar";
import ReactDOM from "react-dom";
import UserConfirmation from "../shared/common/UserConfirmation";
import { AuthorizedCarrier } from "../../models/financing-request/AuthorizedCarrier";
import AuthorizedCarrierTransportOption from "../../models/financing-request/transport-option/AuthorizedCarrierTransportOption";
import AnnualAdministration from "../../models/admin/annual-administration/AnnualAdministration";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import DecisionDocument from "../../models/financing-request/DecisionDocument";

interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function FinancingRequestPage({ match, history }: Props) {
  const { user } = useContext(UserContext);
  const { id } = match.params;
  const [financingRequest, setFinancingRequest] = React.useState<
    FinancingRequest
  >({
    classificationMark: "",
    registerNumber: "",
    dateFrom: null,
    dateTo: null,
    schoolCarriers: new Array<SchoolCarrier>(),
    authorizedCarriers: new Array<AuthorizedCarrier>(),
    students: new Array<FinancingRequestStudent>(),
    studentsData: new Array<FinancingRequestStudentData>()
  } as FinancingRequest);

  const [annualAdministration, setAnualAdministration] = React.useState<
    AnnualAdministration
  >({} as AnnualAdministration);

  const [updateStudentData, setUpdateStudentData] = React.useState<boolean>(
    false
  );
  const [showWarningMessage, setShowWarningMessage] = React.useState<boolean>(
    true
  );

  const [decisionDocument, setDecisionDocument] = useState<DecisionDocument>();
  const [schoolCarriersData, setSchoolCarriersData] = useState<SchoolCarrier[]>();
  const [authorizedCarriersData, setAuthorizedCarriersData] = useState<AuthorizedCarrier[]>();
  const [studentsData, setStudentsData] = useState<FinancingRequestStudentData[]>();
  const [canLoadDocumentPage, setCanLoadDocumentPage] = useState<boolean>(false);

  const {
    getTransportOptionDataInfo,
    getFoodOptionDataInfo,
    getTeachingAidOptionDataInfo,
    getAnnualPricingInfo,
  } = useStudentDataInfo({});

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  useEffect(() => {
    if (updateStudentData) {
      handleUpdateStudentsDataOnStudentsPick();
      setUpdateStudentData(false);
    }
  }, [updateStudentData]);

  useEffect(() => {
    async function getFinancingRequestForEdit(id: string) {
      const apiClient = new FinancingRequestApiClient();

      var newFinancingRequest = await apiClient.getFinancingRequestForEdit(+id);
      var pricing = await getAnnualPricingInfo(financingRequest.schoolYearId);
      if (pricing !== null && pricing == undefined) {
        console.log("getFinancingRequestForEdit - no price");
      }

      newFinancingRequest = {
        ...newFinancingRequest,
        studentsData: newFinancingRequest.studentsData.map((studentData) => {
          var editStudentData = { ...studentData };
          editStudentData.transportOptionInfo = editStudentData.transportOption
            ? getTransportOptionDataInfo(
              editStudentData.transportOption,
              financingRequest.currency,
              financingRequest.schoolCarriers,
              financingRequest.authorizedCarriers
            )
            : "-";
          editStudentData.foodOptionInfo = editStudentData.foodOption
            ? getFoodOptionDataInfo(editStudentData.foodOption)
            : "-";

          editStudentData.teachingAidOptionInfo = editStudentData.teachingAidOption
            ? getTeachingAidOptionDataInfo(
              editStudentData.teachingAidOption,
              editStudentData.student.programType,
              editStudentData.student.specialNeedType,
              financingRequest.currency,
              pricing
            )
            : "-";
          return editStudentData;
        }),
      };

      setFinancingRequest((prev) => ({ ...prev, ...newFinancingRequest }));
    }
    if (id) {
      getFinancingRequestForEdit(id);
    }
  }, []);

  async function createFinancingRequestDraft() {
    const apiClient = new FinancingRequestApiClient();
    var financingRequestId = await apiClient
      .createFinancingRequestDraft(financingRequest)
      .then(function (response) {
        openSnackbar("Zahtjev je privremeno pohranjen.", "success");
        return response;
      });

    var newFinancingRequest = await apiClient.getFinancingRequestForEdit(
      financingRequestId
    );


    var pricing = await getAnnualPricingInfo(financingRequest.schoolYearId);

    newFinancingRequest = {
      ...newFinancingRequest,
      studentsData: newFinancingRequest.studentsData.map((studentData) => {
        var editStudentData = { ...studentData };
        editStudentData.transportOptionInfo = editStudentData.transportOption
          ? getTransportOptionDataInfo(
            editStudentData.transportOption,
            financingRequest.currency,
            financingRequest.schoolCarriers,
            financingRequest.authorizedCarriers
          )
          : "-";
        editStudentData.foodOptionInfo = editStudentData.foodOption
          ? getFoodOptionDataInfo(editStudentData.foodOption)
          : "-";
        editStudentData.teachingAidOptionInfo = editStudentData.teachingAidOption
          ? getTeachingAidOptionDataInfo(
            editStudentData.teachingAidOption,
            editStudentData.student.programType,
            editStudentData.student.specialNeedType,
            financingRequest.currency,
            pricing
          )
          : "-";
        return editStudentData;
      }),
    };

    setFinancingRequest({ ...newFinancingRequest });
  }

  async function updateFinancingRequestDraft() {
    const apiClient = new FinancingRequestApiClient();
    await apiClient
      .updateFinancingRequestDraft(financingRequest)
      .then(function (response) {
        openSnackbar("Zahtjev je privremeno pohranjen.", "success");
      });

    if (financingRequest.id) {
      var updatedFinancingRequest = await apiClient.getFinancingRequestForEdit(
        financingRequest.id
      );

      updatedFinancingRequest = {
        ...updatedFinancingRequest,
        studentsData: updatedFinancingRequest.studentsData.map(
          (studentData) => {
            var editStudentData = { ...studentData };
            editStudentData.transportOptionInfo = editStudentData.transportOption
              ? getTransportOptionDataInfo(
                editStudentData.transportOption,
                financingRequest.currency,
                financingRequest.schoolCarriers,
                financingRequest.authorizedCarriers
              )
              : "-";
            editStudentData.foodOptionInfo = editStudentData.foodOption
              ? getFoodOptionDataInfo(editStudentData.foodOption)
              : "-";
            editStudentData.teachingAidOptionInfo = editStudentData.teachingAidOption
              ? getTeachingAidOptionDataInfo(
                editStudentData.teachingAidOption,
                editStudentData.student.programType,
                editStudentData.student.specialNeedType,
                financingRequest.currency,
                annualAdministration
              )
              : "-";
            return editStudentData;
          }
        ),
      };

      setFinancingRequest({ ...updatedFinancingRequest });
    }
  }

  const handleUpdateFinancingRequest = (
    finRequest: FinancingRequestBasicInfo
  ) => {
    setFinancingRequest((prevFin) => ({
      ...prevFin,
      classificationMark: finRequest.classificationMark,
      registerNumber: finRequest.registerNumber,
      dateFrom: finRequest.dateFrom,
      dateTo: finRequest.dateTo,
    }));
  };

  const handleUpdateDocuments = (
    decisionDocument: DecisionDocument,
    schoolCarriersData: SchoolCarrier[],
    authorizedCarriersData: AuthorizedCarrier[],
    studentsData: FinancingRequestStudentData[]
  ) => {
    setDecisionDocument(decisionDocument);
    setSchoolCarriersData(schoolCarriersData);
    setAuthorizedCarriersData(authorizedCarriersData);
    setStudentsData(studentsData);
  };

  const handleSetShowWarningMessage = (
    showModal: boolean
  ) => {
    setShowWarningMessage(showModal);
  };

  const handleUpdateSchoolCarrier = (newSchoolCarriers: SchoolCarrier[]) => {
    setFinancingRequest((prevFin) => ({
      ...prevFin,
      schoolCarriers: newSchoolCarriers,
      studentsData: prevFin.studentsData.map((studentData) => {
        // Update studentsData transport
        if (
          studentData.transportOption &&
          studentData.transportOption.transportOptionType &&
          studentData.transportOption.transportOptionType ==
          TransportOptionType.SchoolCarrier
        ) {
          const transportSchoolCarrier = studentData.transportOption as SchoolCarrierTransportOption;
          let newTransport = { ...transportSchoolCarrier };
          let newStudentData = { ...studentData };
          if (
            newSchoolCarriers.filter(
              (nSC) => nSC.vehicleType == transportSchoolCarrier.vehicleType
            ).length == 0 ||
            newSchoolCarriers.filter(
              (nSC) =>
                nSC.vehicleType != transportSchoolCarrier.vehicleType &&
                nSC.registrationPlate ==
                transportSchoolCarrier.registrationPlate
            ).length > 0
          ) {
            newTransport.vehicleType = undefined;
            newTransport.registrationPlate = undefined;
            newStudentData = {
              ...newStudentData,
              transportOption: newTransport,
            };
          }

          if (
            newSchoolCarriers.filter(
              (nSC) =>
                nSC.registrationPlate ==
                transportSchoolCarrier.registrationPlate
            ).length == 0
          ) {
            newTransport.registrationPlate = undefined;
            newStudentData = {
              ...newStudentData,
              transportOption: newTransport,
            };

            if (newSchoolCarriers.length == 0) {
              newStudentData = {
                ...newStudentData,
                transportOption: undefined,
              };
            }
          }

          return newStudentData;
        }
        return studentData;
      }),
    }));
  };

  const handleUpdateAuthorizedCarrier = (
    newAuthorizedCarriers: AuthorizedCarrier[]
  ) => {
    setFinancingRequest((prevFin) => ({
      ...prevFin,
      authorizedCarriers: newAuthorizedCarriers,
      studentsData: prevFin.studentsData.map((studentData) => {
        // Update studentsData transport
        if (
          studentData.transportOption &&
          studentData.transportOption.transportOptionType &&
          studentData.transportOption.transportOptionType ==
          TransportOptionType.AuthorizedCarrier
        ) {
          const transportAuthorizedCarrier = studentData.transportOption as AuthorizedCarrierTransportOption;
          let newTransport = { ...transportAuthorizedCarrier };
          let newStudentData = { ...studentData };

          if (newAuthorizedCarriers.length == 0) {
            newStudentData = {
              ...newStudentData,
              transportOption: undefined,
            };
          } else if (
            newAuthorizedCarriers.filter(
              (nAC) =>
                nAC.id != null &&
                nAC.id == transportAuthorizedCarrier.authorizedCarrierId
            ).length == 0
          ) {
            newTransport.authorizedCarrierId = undefined;
            newStudentData = {
              ...newStudentData,
              transportOption: newTransport,
            };
          }
          return newStudentData;
        }
        return studentData;
      }),
    }));
  };

  const handleStudentsChange = (students: FinancingRequestStudent[]) => {
    setFinancingRequest((prevFin) => ({
      ...prevFin,
      students: students,
    }));

    handleUpdateStudentsDataOnStudentsPick();
  };

  const handleUpdateStudentsOnDateChange = (dateFrom: Date) => {
    async function getStudents() {
      const apiClient = new FinancingRequestApiClient();
      const students = await apiClient.getStudentsForEditFinancingRequest(
        +id,
        dateFrom
      );

      let newStudents = students.map((student) => {
        var selectedStudent = financingRequest.students.find(
          (frs) => frs.isSelected && frs.id == student.id
        );
        if (selectedStudent) {
          return { ...student, isSelected: student.isEnabled };
        }
        return student;
      });

      setFinancingRequest((prevFin) => ({
        ...prevFin,
        students: newStudents,
      }));

      setUpdateStudentData(true);
    }

    if (
      financingRequest.students != null &&
      financingRequest.students.length > 0 &&
      financingRequest.dateFrom != dateFrom
    ) {
      getStudents();
    }
  };

  const handleUpdateStudentsData = (
    newStudentsData: FinancingRequestStudentData[]
  ) => {
    setFinancingRequest((prevFin) => ({
      ...prevFin,
      studentsData: [...newStudentsData],
    }));
  };

  const handleUpdateStudentsDataOnStudentsPick = () => {
    const studentsDataState = financingRequest.studentsData;
    const students = financingRequest.students;
    if (studentsDataState.length == 0) {
      let newStudentsData = new Array<FinancingRequestStudentData>();
      students.forEach((student) => {
        if (student.isSelected) {
          newStudentsData.push({
            student: student,
            currency: financingRequest.currency
          });
        }
      });

      handleUpdateStudentsData(newStudentsData);
    } else {
      let selectedStudentsData = new Array<FinancingRequestStudentData>();
      let notSelectedStudentsData = new Array<FinancingRequestStudentData>();
      students.forEach((student) => {
        if (student.isSelected) {
          const studentData = studentsDataState.find(
            (sd) => sd.student.id == student.id
          );
          if (studentData != null) {
            selectedStudentsData.push({ ...studentData, student: student });
          } else {
            selectedStudentsData.push({
              student: student,
              currency: financingRequest.currency
            });
          }
        } else {
          const studentData = studentsDataState.find(
            (sd) => sd.student.id == student.id
          );
          if (studentData != null) {
            notSelectedStudentsData.push(studentData);
          }
        }
      });

      let formerPassengers = new Array<FinancingRequestStudent>();
      notSelectedStudentsData.forEach((notSelected) => {
        //get passengers for deselected personal students
        if (
          notSelected.transportOption &&
          notSelected.transportOption.transportOptionType &&
          notSelected.transportOption.transportOptionType ==
          TransportOptionType.Personal
        ) {
          const personalTransport = notSelected.transportOption as PersonalTransportOption;
          if (personalTransport.passengers) {
            personalTransport.passengers.forEach((passenger) => {
              formerPassengers.push(passenger);
            });
          }
        }
        if (
          notSelected.transportOption &&
          notSelected.transportOption.transportOptionType &&
          notSelected.transportOption.transportOptionType ==
          TransportOptionType.PersonalPassenger
        ) {
          const personalPassengerTransport = notSelected.transportOption as PersonalPassengerTransportOption;
          if (personalPassengerTransport.personalStudent) {
            if (
              formerPassengers.find((fp) => fp.id == notSelected.student.id) ==
              null
            ) {
              formerPassengers.push(notSelected.student);
            }
          }
        }
      });
      selectedStudentsData.forEach((selected, index) => {
        let student = { ...selected };
        if (
          selected.transportOption &&
          selected.transportOption.transportOptionType &&
          selected.transportOption.transportOptionType ==
          TransportOptionType.Personal
        ) {
          const personalTransport = selected.transportOption as PersonalTransportOption;
          if (personalTransport.passengers) {
            personalTransport.passengers.forEach(
              (passenger, index, passengers) => {
                if (
                  formerPassengers.find((fp) => fp.id == passenger.id) != null
                ) {
                  passengers.splice(index, 1);
                }
              }
            );
          }
        }

        if (
          selected.transportOption &&
          selected.transportOption.transportOptionType &&
          selected.transportOption.transportOptionType ==
          TransportOptionType.PersonalPassenger
        ) {
          const personalPassengerTransport = selected.transportOption as PersonalPassengerTransportOption;
          //update passengers for deselected personal students
          if (
            formerPassengers.find(
              (former) => former.id == selected.student.id
            ) != null
          ) {
            student.transportOption = undefined;
          }
        }

        student.transportOptionInfo = student.transportOption
          ? getTransportOptionDataInfo(
            student.transportOption,
            financingRequest.currency,
            financingRequest.schoolCarriers,
            financingRequest.authorizedCarriers
          )
          : "-";
        student.foodOptionInfo = student.foodOption
          ? getFoodOptionDataInfo(student.foodOption)
          : "-";
        student.teachingAidOptionInfo = student.teachingAidOption
          ? getTeachingAidOptionDataInfo(
            student.teachingAidOption,
            student.student.programType,
            student.student.specialNeedType,
            financingRequest.currency,
            annualAdministration
          )
          : "-";
        selectedStudentsData[index] = student;
      });

      handleUpdateStudentsData(selectedStudentsData);
    }
  };

  const validateDocuments = (): boolean => {
    let errors: string[] = [];

    studentsData && studentsData.forEach(studentData => {
      if (studentData.transportOption && studentData.transportOption.transportOptionType
        &&
        (studentData.transportOption.transportOptionType == TransportOptionType.Personal || studentData.transportOption.transportOptionType == TransportOptionType.CompanionInPublicTransport)) {
        const studentDocs = studentData.student.uploadedDocuments;
        if (!studentDocs || studentDocs.length === 0) {
          errors.push(`Obavezan unos dokumentacije za prijevoz za učenika: ${studentData.student.lastName} ${studentData.student.firstName}`);
        }
      }
    });

    schoolCarriersData && schoolCarriersData.forEach(carrier => {
      if (carrier.id != null) {
        const carrierDocs = carrier.uploadedDocuments;
        if (!carrierDocs || carrierDocs.length === 0) {
          errors.push(`Obavezan unos dokumentacije za školskog prijevoznika: ${carrier.registrationPlate}`);
        }
      }
    });

    authorizedCarriersData && authorizedCarriersData.forEach(carrier => {
      if (carrier.id != null) {
        const carrierDocs = carrier.uploadedDocuments;
        if (!carrierDocs || carrierDocs.length === 0) {
          errors.push(`Obavezan unos dokumentacije za ovlaštenog prijevoznika: ${carrier.name}`);
        }
      }
    });

    if (!decisionDocument) {
      errors.push(`Obavezan unos dokumentacije za odluku`);
    }

    if (errors.length > 0) {
      openSnackbar(errors.join('\n'), "error");
      return false;
    }

    return true;
  };

  const handleFinish = () => {
    if (!validateDocuments()) {
      return;
    }
    async function createFinancingRequest() {
      const apiClient = new FinancingRequestApiClient();
      await apiClient.createFinancingRequest(financingRequest);

      setShowWarningMessage(false);
      history.push(financingRequestsUrl());
    }

    async function finishFinancingRequest() {
      const apiClient = new FinancingRequestApiClient();
      await apiClient.finishFinancingRequest(financingRequest);

      setShowWarningMessage(false);
      history.push(financingRequestsUrl());
    }

    if (id || (financingRequest.id && financingRequest.id > 0)) {
      finishFinancingRequest();
    } else {
      createFinancingRequest();
    }
  };

  // const handleSaveDraft = async () => {
  //   setCanLoadDocumentPage(false);
  //   if (financingRequest.id) {
  //     let doUpdatePromise = () => Promise.resolve(updateFinancingRequestDraft());
  //     doUpdatePromise().then(() => { setCanLoadDocumentPage(true); });
  //   } else {
  //     let doCreatePromise = () => Promise.resolve(createFinancingRequestDraft());
  //     doCreatePromise().then(() => { setCanLoadDocumentPage(true); });
  //   }
  // };

  const handleSaveDraft = async () => {
    setCanLoadDocumentPage(false);

    if (financingRequest.id) {
      await updateFinancingRequestDraft();
    } else {
      await createFinancingRequestDraft();
    }

    setCanLoadDocumentPage(true);
  };


  const consoleLogChange = () => {
    console.log(financingRequest);
  };

  const getTitles = (): string[] => {
    return [
      "Kreiranje zahtjeva",
      "Odabir učenika",
      "Unos podataka",
      "Potvrda zahtjeva",
      "Predaja zahtjeva"
    ];
  };

  const deleteFinancingRequest = (id: number) => async () => {
    const apiClient = new FinancingRequestApiClient();
    const response = await apiClient.deleteFinancingRequest(id);
    setShowWarningMessage(false);

    history.push(financingRequestsUrl());
  };

  useEffect(() => {
    async function getPricingRequest() {
      const apiPaymentClient = new PaymentRequestApiClient();
      const pricingRequest = await apiPaymentClient.getAnualPricing();

      var firstStudent = financingRequest.students[0];
      if (firstStudent !== undefined) {
        var schoolYearId = financingRequest.schoolYearId > 0 ? financingRequest.schoolYearId : firstStudent.schoolYearId;
        setAnualAdministration(pricingRequest.filter((price) => {
          return price.schoolYearId === schoolYearId
        })[0]);
      }
    }
    getPricingRequest();
  }, [financingRequest]);

  const hasUnsavedChanges =
    showWarningMessage &&
    (
      Boolean(financingRequest.classificationMark && financingRequest.classificationMark.trim()) ||
      Boolean(financingRequest.registerNumber && financingRequest.registerNumber.trim()) ||
      financingRequest.dateFrom !== null ||
      (financingRequest.schoolCarriers && financingRequest.schoolCarriers.length > 0) ||
      (financingRequest.authorizedCarriers && financingRequest.authorizedCarriers.length > 0) ||
      (financingRequest.students && financingRequest.students.some(s => s.isSelected)) ||
      (financingRequest.studentsData && financingRequest.studentsData.length > 0)
    );


  return (
    <>
      <StepperComponent
        steps={getTitles()}
        previousButtonText={"Prethodni korak"}
        nextButtonText={"Sljedeći korak"}
        lastStepButtonText={"Potvrdi zahtjev"}
        confirmationModalTitle={"Predaja zahtjeva"}
        confirmationModalMessage={
          "Potvrdom ove akcije šaljete godišnji zahtjev na daljnju obradu Ministarstvu znanosti i obrazovanja. Jeste li sigurni da želite predati zatjev?"
        }
        confirmationModalCancelBtnText="Odustani"
        confirmationModalOkBtnText="Predaj zahtjev"
        onFinish={handleFinish}
        saveBtn
        saveBtnText="Privremena pohrana"
        saveModalTitle="Privremena pohrana zahtjeva"
        saveModalMessage="Potvrdom ove akcije šaljete godišnji zahtjev na privremenu pohranu. Jeste li sigurni da želite privremeno pohraniti zatjev?"
        saveModalOkBtnText="Pohrani zahtjev"
        saveModalCancelBtnText="Odustani"
        onSave={handleSaveDraft}
        deleteBtn={financingRequest.id != null && financingRequest.id > 0}
        onDelete={
          financingRequest.id != null && financingRequest.id > 0
            ? deleteFinancingRequest(financingRequest.id)
            : undefined
        }
        isFinancingRequest={true}
      >
        <FinancingRequestBasicInfoStep
          financingRequest={financingRequest}
          updateFinancingRequest={handleUpdateFinancingRequest}
          updateSchoolCarrier={handleUpdateSchoolCarrier}
          updateAuthorizedCarrier={handleUpdateAuthorizedCarrier}
          updateStudents={handleUpdateStudentsOnDateChange}
        />
        <FinancingRequestStudentStep
          dateFrom={financingRequest.dateFrom}
          students={financingRequest.students}
          onChange={handleStudentsChange}
        />
        <FinancingRequestStudentDataStep
          schoolCarriers={financingRequest.schoolCarriers}
          authorizedCarriers={financingRequest.authorizedCarriers}
          students={financingRequest.students}
          studentsDataState={financingRequest.studentsData}
          currency={financingRequest.currency}
          // schoolYearName={"financingRequest.schoolYearId"}
          // schoolYearId={financingRequest.schoolYearId}
          annualPricing={annualAdministration}
          updateStudentsData={handleUpdateStudentsData}
        />
        <FinancingRequestDataConfirmationStep
          studentsData={financingRequest.studentsData}
        />
        <FinancingRequestDataSubmissionStep
          financingRequestId={financingRequest.id || 0}
          handleSetShowWarningMessage={handleSetShowWarningMessage}
          handleUpdateDocuments={handleUpdateDocuments}
          canLoadDocumentPage={canLoadDocumentPage}
        />


      </StepperComponent>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
      <Prompt
        when={hasUnsavedChanges}
        message="Potvrdom ove akcije izgubit ćete podatke koje niste pohranili na trenutnom zahtjevu. Jeste li sigurni da želite odustati od ispunjavanja zahtjeva?"
      />
    </>
  );
}

const getUserConfirmation = (message: string, callback: any) => {
  const modal = document.createElement("div");
  document.body.appendChild(modal);

  const withCleanup = (answer: boolean) => {
    ReactDOM.unmountComponentAtNode(modal);
    document.body.removeChild(modal);
    callback(answer);
  };

  ReactDOM.render(
    <UserConfirmation
      message={message}
      onCancel={() => withCleanup(false)}
      onConfirm={() => withCleanup(true)}
    />,
    modal
  );
};
